import { getFairHead } from '@/seo/fairHead'
import {
  FAIR_SEO_DEFAULT_LANG,
  fairAbsoluteUrl,
  fairOgImageUrl,
} from '@/seo/fairSeo.defaults'
import {
  buildFairWebsiteJsonLd,
  type FairRouteSeo,
} from '@/seo/routeSeo'

interface FairSeoHeadEntry {
  patch: (input: Record<string, unknown>) => void
}

let seoEntry: FairSeoHeadEntry | null = null

function buildHeadInput(seo: FairRouteSeo): Record<string, unknown> {
  const url = fairAbsoluteUrl(seo.path)
  const image = seo.ogImage ?? fairOgImageUrl()
  const scripts = seo.jsonLdWebsite
    ? [
        {
          type: 'application/ld+json',
          key: 'fair-website-jsonld',
          innerHTML: JSON.stringify(buildFairWebsiteJsonLd()),
        },
      ]
    : []

  return {
    htmlAttrs: {
      lang: FAIR_SEO_DEFAULT_LANG,
    },
    title: seo.title,
    meta: [
      { name: 'description', content: seo.description },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'FAIR' },
      { property: 'og:title', content: seo.title },
      { property: 'og:description', content: seo.description },
      { property: 'og:url', content: url },
      { property: 'og:image', content: image },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: seo.title },
      { name: 'twitter:description', content: seo.description },
      { name: 'twitter:image', content: image },
    ],
    link: [{ rel: 'canonical', href: url }],
    script: scripts,
  }
}

/** Apply or replace the single route-level SEO head entry. */
export function applyFairSeo(seo: FairRouteSeo): void {
  const input = buildHeadInput(seo)
  if (seoEntry) {
    seoEntry.patch(input)
    return
  }
  seoEntry = getFairHead().push(input) as FairSeoHeadEntry
}

/** Alias for setup() contexts — same single-entry behavior. */
export function useFairSeo(seo: FairRouteSeo): void {
  applyFairSeo(seo)
}
